import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Factory, Warning, CaretDown, CaretUp, CheckCircle, Clock, Package, Truck } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MaterialsTraceabilityPanel } from "@/components/case/MaterialsTraceabilityPanel";
import ShipmentsList from "@/components/case/ShipmentsList";

/**
 * LabProductionTab — doctor's single pane for everything lab-produced
 * on a case.
 *
 * Shows the chronological spec history (parent + every refinement
 * fabrication), each one with its manufacturing status, planner-authored
 * spec, production execution, and any concerns raised. Below that,
 * the Materials Traceability panel (which lot/batch was used).
 *
 * Why a dedicated tab: the manufacturing spec was previously hidden on
 * the Submission tab — wrong place conceptually (Submission = what
 * doctor sent, this = what lab produced). Refinements + retainers will
 * accumulate over the case lifecycle, so a single growing card stack on
 * Submission would have made that tab harder to navigate over time.
 *
 * Backend contract:
 *   GET  /api/cases/{id}/manufacturing/history   → { items: [...] }
 *   POST /api/cases/{id}/manufacturing/flag      → creates case_issue
 *
 * Per-card "Raise a concern" lets the doctor flag what they OBSERVE
 * (count off, fit issue, defect, patient discomfort, tracking concern,
 * other) — not what they think the lab did. The doctor doesn't pick
 * materials, so they shouldn't have to compare SKUs. The production
 * team owns root-cause diagnosis on the receiving end.
 */
export default function LabProductionTab({ caseId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [flagFor, setFlagFor] = useState(null); // {case_id, case_code}

    const reload = async () => {
        try {
            const r = await api.get(`/cases/${caseId}/manufacturing/history`);
            setData(r.data);
        } catch (e) {
            // Silently render empty state — likely the case never reached production.
            setData({ items: [] });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!caseId) return;
        reload();
    }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
        return (
            <div className="bg-card border border-border rounded-2xl p-8 text-center text-muted-foreground text-sm" data-testid="lab-tab-loading">
                Loading manufacturing details…
            </div>
        );
    }

    const items = data?.items || [];

    if (items.length === 0) {
        return (
            <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center" data-testid="lab-tab-empty">
                <Factory size={32} weight="duotone" className="mx-auto opacity-50 mb-3 text-primary" />
                <div className="font-display text-lg font-semibold mb-1">No fabrications yet</div>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    Manufacturing details appear here once your planning team finalises the spec
                    and the lab starts producing aligners. Refinements and retainers will join
                    this view automatically as they happen.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="lab-production-tab">
            <div className="flex items-start gap-3 px-1">
                <Factory size={22} weight="duotone" className="text-primary mt-0.5 shrink-0" />
                <div className="min-w-0">
                    <h2 className="font-display text-xl font-semibold">Lab & Production</h2>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        Every fabrication run for this patient — initial aligners, refinements, and retainers.
                        Notice something off about a shipment? Click <span className="font-semibold">Raise a concern</span> on any card and your production team will look into it.
                    </p>
                </div>
            </div>

            {items.map((it) => (
                <FabricationCard
                    key={it.case_id}
                    item={it}
                    onFlag={() => setFlagFor({ case_id: it.case_id, case_code: it.case_code })}
                    onChange={reload}
                />
            ))}

            {/* iter97.36 — MaterialsTraceabilityPanel hidden from the doctor.
                Per Invisique policy, material identity (lot, supplier,
                manufacturer) is internal lab information and is never
                surfaced to doctors. Lot traceability stays available for
                Planner / Lab / Admin via the COE surfaces. */}
            {false && <MaterialsTraceabilityPanel caseId={caseId} />}

            <FlagMismatchDialog
                open={!!flagFor}
                onClose={() => setFlagFor(null)}
                caseRootId={caseId}
                specCaseId={flagFor?.case_id}
                specCaseCode={flagFor?.case_code}
                onSubmitted={reload}
            />
        </div>
    );
}


/* ─── Card per fabrication run ────────────────────────────────────────── */

const STATE_META = {
    awaiting: {
        label: "Awaiting production",
        bg: "bg-amber-100", icon: "text-amber-700",
        pill: "bg-amber-100 text-amber-800", Icon: Clock,
    },
    in_progress: {
        label: "In manufacturing",
        bg: "bg-orange-100", icon: "text-orange-700",
        pill: "bg-orange-100 text-orange-800", Icon: Factory,
    },
    completed: {
        label: "Manufacturing complete",
        bg: "bg-emerald-100", icon: "text-emerald-700",
        pill: "bg-emerald-100 text-emerald-800", Icon: CheckCircle,
    },
};

function execStateFor(caseStatus, exec_) {
    if (exec_?.completed_at) return "completed";
    if (exec_?.started_at) return "in_progress";
    if (["in_production", "shipped", "delivered", "in_treatment",
         "treatment_started", "refinement_requested", "completed"]
         .includes(caseStatus)) return "in_progress";
    return "awaiting";
}


function FabricationCard({ item, onFlag, onChange }) {
    const [open, setOpen] = useState(item.is_root);  // root expanded by default, refinements collapsed
    const spec = item.spec || {};
    const exec_ = item.execution || {};
    const state = execStateFor(item.status, exec_);
    const sm = STATE_META[state];
    const openFlags = (item.flags || []).filter((f) => f.status === "open");
    const shipments = item.shipments || [];
    // iter80 — "Awaiting receipt" summary chip on the card header tells
    // the doctor at a glance whether they have any pending packages.
    const awaitingReceipt = shipments.filter((s) => s.status !== "received").length;

    return (
        <div
            className={`rounded-2xl border bg-card overflow-hidden ${openFlags.length ? "border-rose-300 ring-1 ring-rose-200" : "border-border"}`}
            data-testid={`fab-card-${item.case_id}`}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-secondary/30 transition"
                data-testid={`fab-card-${item.case_id}-toggle`}
            >
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${sm.bg}`}>
                        <Factory size={18} weight="fill" className={sm.icon} />
                    </div>
                    <div className="text-left min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-display font-bold text-sm sm:text-base">
                                {item.case_kind === "refinement" ? "Refinement fabrication" : "Initial fabrication"}
                            </span>
                            {item.case_code && (
                                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                                    {item.case_code}
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${sm.pill}`}>
                                <sm.Icon size={9} weight="fill" /> {sm.label}
                            </span>
                            <span className="text-foreground/60">·</span>
                            <span>{specSummary(spec)}</span>
                            {openFlags.length > 0 && (
                                <>
                                    <span className="text-foreground/60">·</span>
                                    <span
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-800"
                                        data-testid={`fab-card-${item.case_id}-flag-count`}
                                    >
                                        <Warning size={9} weight="fill" /> {openFlags.length} concern{openFlags.length === 1 ? "" : "s"}
                                    </span>
                                </>
                            )}
                            {awaitingReceipt > 0 && (
                                <>
                                    <span className="text-foreground/60">·</span>
                                    <span
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800"
                                        data-testid={`fab-card-${item.case_id}-awaiting-count`}
                                        title="Shipments not yet confirmed received at your clinic"
                                    >
                                        <Truck size={9} weight="fill" /> {awaitingReceipt} awaiting receipt
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                {open ? <CaretUp size={14} className="text-muted-foreground" /> : <CaretDown size={14} className="text-muted-foreground" />}
            </button>

            {open && (
                <div className="border-t border-border p-5 space-y-5">
                    {/* Spec */}
                    <div>
                        <div className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground mb-2">
                            Specification (planner-authored)
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            <SpecRow label="Aligner stages" value={spec.aligner_stages_count || "—"} />
                            <SpecRow label="Sheets per stage" value={spec.sheets_per_stage || "—"} />
                            <SpecRow label="Arches" value={archLabel(spec.arches)} />
                            <SpecRow label="Retainers" value={spec.retainer_count ?? "—"} />
                            {/* iter97.36 — Material rows hidden from doctor view.
                                Material is an internal lab decision and is never
                                exposed to the doctor. Planner / Lab / Admin still
                                see this on their own surfaces. */}
                            {spec.notes && (
                                <SpecRow label="Lab notes" value={spec.notes} fullWidth />
                            )}
                        </div>
                        {/* iter82 — planner-authored phasing surfaces to the
                            doctor on the spec card too. When phases are set,
                            the doctor knows in advance how many separate
                            shipments to expect (and thus how many
                            confirm-receipt clicks they'll do over the
                            lifecycle of this fabrication). */}
                        {(spec.phases || []).length > 0 && (
                            <div className="mt-3 rounded-lg border border-border bg-secondary/30 p-2.5">
                                <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1.5 inline-flex items-center gap-1">
                                    Planned dispatch · {spec.phases.length} phases
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {spec.phases.map((p, idx) => (
                                        <span
                                            key={idx}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-card border border-border text-[11px] font-semibold"
                                            data-testid={`fab-card-${item.case_id}-phase-${idx}`}
                                        >
                                            Phase {idx + 1}: stages {p.stage_from}–{p.stage_to}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* iter80 — Shipments. Renders the per-shipment timeline:
                        dispatch date, received-at-clinic date, "Confirm
                        received" button when applicable. Single-shipment
                        cases render 1 row; phased cases render N. */}
                    <div>
                        <div className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground mb-2">
                            Shipments
                        </div>
                        <ShipmentsList
                            caseId={item.case_id}
                            shipments={shipments}
                            onChange={onChange}
                        />
                    </div>

                    {/* Execution */}
                    {(exec_.started_at || exec_.completed_at) && (
                        <div>
                            <div className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground mb-2">
                                Execution (production team)
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                {exec_.started_at && (
                                    <SpecRow label="Manufacturing started" value={fmtDateTime(exec_.started_at)} Icon={Clock} />
                                )}
                                {exec_.completed_at && (
                                    <SpecRow label="Manufacturing completed" value={fmtDateTime(exec_.completed_at)} Icon={CheckCircle} />
                                )}
                                {/* iter97.36 — actual material rows hidden from doctor view. */}
                            </div>
                        </div>
                    )}

                    {/* Existing concerns on this card */}
                    {(item.flags || []).length > 0 && (
                        <div>
                            <div className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground mb-2">
                                Concerns raised ({item.flags.length})
                            </div>
                            <div className="space-y-2">
                                {item.flags.map((f) => (
                                    <FlagRow key={f.id} flag={f} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Raise concern CTA */}
                    <div className="pt-2 flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-xs text-muted-foreground italic">
                            Notice something off about this shipment — aligner count, fit, defect, or patient discomfort? Your production team will look into it.
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={onFlag}
                            className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800"
                            data-testid={`fab-card-${item.case_id}-flag-btn`}
                        >
                            <Warning size={14} className="mr-1.5" weight="fill" />
                            Raise a concern
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}


function FlagRow({ flag }) {
    const isOpen = flag.status === "open";
    return (
        <div
            className={`rounded-lg border p-3 text-sm ${isOpen ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"}`}
            data-testid={`flag-row-${flag.id}`}
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        isOpen ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"
                    }`}>
                        {isOpen ? <Warning size={9} weight="fill" /> : <CheckCircle size={9} weight="fill" />}
                        {isOpen ? `Open · ${flag.severity}` : "Resolved"}
                    </span>
                    <span className="text-xs text-muted-foreground">{fmtDateTime(flag.created_at)}</span>
                </div>
                {flag.raised_by_name && (
                    <span className="text-xs text-muted-foreground">Raised by {flag.raised_by_name}</span>
                )}
            </div>
            <p className="mt-2 text-sm">{flag.description}</p>
            {!isOpen && flag.resolution_note && (
                <p className="mt-2 text-xs text-emerald-900 italic border-t border-emerald-200 pt-2">
                    Resolution: {flag.resolution_note}
                </p>
            )}
        </div>
    );
}


/* ─── Concern dialog ──────────────────────────────────────────────────── */

// 6 pre-canned concern categories. The user picks one (defaults to
// "other"), then fills in the free-text description. Each type is
// stored on the case_issue as `issue_subtype` so admin/production
// dashboards can filter by category later.
const CONCERN_TYPES = [
    { id: "count_mismatch",     label: "Aligner count doesn't match plan",      hint: "Got fewer or more aligners than promised" },
    { id: "physical_defect",    label: "Physical defect",                       hint: "Sharp edges, cracked, malformed, etc." },
    { id: "fit_problem",        label: "Fit problem",                           hint: "Loose, tight, doesn't seat correctly" },
    { id: "patient_discomfort", label: "Patient discomfort beyond expected",   hint: "Unusual pain or pressure on fitment" },
    { id: "tracking_concern",   label: "Stage tracking concern",                hint: "Teeth not moving per plan" },
    { id: "other",              label: "Other concern",                         hint: "Anything else you want production to know" },
];

function FlagMismatchDialog({ open, onClose, caseRootId, specCaseId, specCaseCode, onSubmitted }) {
    const [concernType, setConcernType] = useState("other");
    const [description, setDescription] = useState("");
    const [severity, setSeverity] = useState("medium");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setConcernType("other");
            setDescription("");
            setSeverity("medium");
        }
    }, [open]);

    const submit = async () => {
        const text = description.trim();
        if (text.length < 4) {
            toast.error("Add a bit more detail (at least 4 characters).");
            return;
        }
        try {
            setSubmitting(true);
            await api.post(`/cases/${caseRootId}/manufacturing/flag`, {
                description: text,
                severity,
                issue_subtype: concernType,
                spec_case_id: specCaseId,
            });
            toast.success("Concern raised. Your production team will review it.");
            onSubmitted?.();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't raise the concern. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg" data-testid="flag-mismatch-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Warning size={18} weight="fill" className="text-rose-600" />
                        Raise a concern about this fabrication
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    {specCaseCode && (
                        <div className="text-xs text-muted-foreground">
                            Concern about fabrication <span className="font-mono font-semibold">{specCaseCode}</span>.
                        </div>
                    )}

                    {/* Concern type — radio-button-style grid. Defaults to
                        "other" so the dialog is usable even without a
                        category pick. */}
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                            What did you notice?
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {CONCERN_TYPES.map((t) => {
                                const selected = concernType === t.id;
                                return (
                                    <button
                                        key={t.id}
                                        type="button"
                                        onClick={() => setConcernType(t.id)}
                                        className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                                            selected
                                                ? "border-rose-300 bg-rose-50"
                                                : "border-border bg-card hover:bg-secondary/40"
                                        }`}
                                        data-testid={`concern-type-${t.id}`}
                                        title={t.hint}
                                    >
                                        <div className={`flex items-center gap-2 ${selected ? "text-rose-900 font-semibold" : "text-foreground/80"}`}>
                                            <span className={`w-3 h-3 rounded-full border ${selected ? "border-rose-600 bg-rose-600" : "border-border bg-card"}`} />
                                            {t.label}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground mt-0.5 pl-5">{t.hint}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                            Details
                        </label>
                        <Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Describe what you observed — what's off, when you noticed, what the patient is reporting if relevant."
                            rows={3}
                            data-testid="flag-description-input"
                            maxLength={2000}
                        />
                    </div>

                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                            How urgent?
                        </label>
                        <div className="inline-flex items-center gap-1 bg-secondary/60 rounded-full p-1">
                            {[
                                ["low", "Low"],
                                ["medium", "Medium"],
                                ["high", "High"],
                            ].map(([val, label]) => (
                                <button
                                    key={val}
                                    type="button"
                                    onClick={() => setSeverity(val)}
                                    className={`px-3 h-8 rounded-full text-xs font-semibold transition-colors ${
                                        severity === val
                                            ? (val === "high" ? "bg-rose-600 text-white" : "bg-primary text-white")
                                            : "text-foreground/70 hover:text-foreground"
                                    }`}
                                    data-testid={`flag-severity-${val}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <p className="text-xs text-muted-foreground italic border-t border-border pt-3">
                        Your production team will see this immediately in their case-issues queue. They'll reply within the case workflow — no need to follow up by email.
                    </p>

                    <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" onClick={onClose} data-testid="flag-cancel-btn">Cancel</Button>
                        <Button
                            onClick={submit}
                            disabled={submitting || description.trim().length < 4}
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                            data-testid="flag-submit-btn"
                        >
                            <Warning size={14} className="mr-1.5" weight="fill" />
                            {submitting ? "Sending…" : "Raise concern"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}


/* ─── helpers ─────────────────────────────────────────────────────────── */

function specSummary(spec) {
    const parts = [];
    if (spec.aligner_stages_count) parts.push(`${spec.aligner_stages_count} stages`);
    if (spec.arches) parts.push(spec.arches.replace("_", " "));
    if (spec.retainer_count) parts.push(`${spec.retainer_count} retainer${spec.retainer_count === 1 ? "" : "s"}`);
    return parts.join(" · ") || "—";
}

function archLabel(v) {
    if (!v) return "—";
    return { upper: "Upper only", lower: "Lower only", both: "Both arches" }[v] || v;
}

function materialLabel(m) {
    if (!m) return "—";
    return `${m.name || ""} (${m.sku || "—"})`;
}

function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true });
    } catch {
        return iso;
    }
}


function SpecRow({ label, value, fullWidth, Icon }) {
    return (
        <div className={fullWidth ? "sm:col-span-2" : ""}>
            <div className="text-[10px] tracking-wider uppercase font-bold text-muted-foreground inline-flex items-center gap-1">
                {Icon && <Icon size={10} weight="duotone" />}
                {label}
            </div>
            <div className="mt-0.5 text-foreground">{value}</div>
        </div>
    );
}
